import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { storeToRefs } from "pinia";
import { SettingScopesEnum, type SelectedRequestUser } from "@simbelapp/order-sdk";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";
import { RequestStatus } from "~/utils/enums/request.enums";
import type { IFooterActions, IGeneric } from "~/utils/interfaces/common-interfaces";
import { useCreateRequestStore } from "~/store/requests/create-request.store";
import { ProjectTypeEnum } from "~/utils/enums/project.enums";
import { useRequestsStore } from "~/store/requests/requests.store";
import type { IGetSelectedRequests } from "~/utils/interfaces/requests-interfaces";
import { useCreateProjectStore } from "~/store/project/create-project.store";
import { ItemType } from "~/utils/enums/common.enums";
import { useAuthStore } from "~/store/auth/auth.store";
import { useCreateCompaniesStore } from "~/store/companies/create-company.store";

export const useRequests = () => {
  const headers = useState<IIndexHeader[]>(() => [
    {
      name: "Demandée le",
      sort: true,
      key: "created_at",
      minWidthPx: 110,
      maxWidthFr: 1.5,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
    {
      name: "Employé",
      sort: true,
      key: "lastname",
      minWidthPx: 201,
      maxWidthFr: 2,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
    {
      name: "Formation",
      sort: true,
      key: "training_title",
      minWidthPx: 259,
      maxWidthFr: 4,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
    {
      name: "Messages",
      sort: false,
      key: "last_comment",
      minWidthPx: 90,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
    {
      name: "Prix (HT)",
      key: "price_excl_tax",
      sort: true,
      minWidthPx: 110,
      maxWidthFr: 1.5,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
    {
      name: "Priorité",
      key: "priority",
      sort: true,
      minWidthPx: 90,
      maxWidthFr: 1,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
    {
      name: "Manager",
      sort: true,
      key: "manager_firstname",
      minWidthPx: 155,
      maxWidthFr: 2,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
    {
      name: "Statut",
      sort: true,
      key: "status",
      minWidthPx: 270,
      maxWidthFr: 3,
      visible: true,
      scope: SettingScopesEnum.RequestsTab,
    },
  ]);

  function statusMapping(value: string) {
    return [
      {
        name: RequestStatus.WAITING_MANAGER_INPUT,
        value: "Indications du manager en attente",
        type: "light_blue",
      },
      {
        name: RequestStatus.WAITING_PRIORITY_CONFIRMATION,
        value: "Confirmation priorité en attente",
        type: "light_blue",
      },
      {
        name: RequestStatus.WAITING_QUALIFICATION,
        value: "Validation RH en attente",
        type: "light_orange",
      },
      {
        name: RequestStatus.WAITING_HR_VALIDATION,
        value: "Validation RH en attente",
        type: "light_orange",
      },
      {
        name: RequestStatus.REQUEST_REFUSED,
        value: "Demande refusée",
        type: "light_red",
      },
      {
        name: RequestStatus.REQUEST_CANCELED,
        value: "Annulée",
        type: "light_red",
      },
      {
        name: RequestStatus.VALIDATED,
        value: "Validée",
        type: "light_green",
      },
      {
        name: RequestStatus.NO_VALIDATION_NEEDED,
        value: "Aucune validation nécessaire",
        type: "grey",
      },
      {
        name: RequestStatus.WAITING_FINANCING_VALIDATION,
        value: "En attente de financement",
        type: "light_purple",
      },
      {
        name: RequestStatus.ON_HOLD,
        value: "En pause",
        type: "grey",
      },
      {
        name: RequestStatus.PLANNED,
        value: "En cours de traitement",
        type: "light_blue",
      },
    ].find((x) => x.name === value);
  }

  function getRequestPriority(): IGeneric[] {
    return [
      {
        key: "p1",
        value: "P1 - Indispensable et urgent",
      },
      {
        key: "p2",
        value: "P2 - Indispensable et non urgent",
      },
      {
        key: "p3",
        value: "P3 - Non indispensable (pourquoi pas)",
      },
      {
        key: "p4",
        value: "P4 - Ne donnons pas suite",
      },
    ];
  }

  const footersActions: IFooterActions[] = [
    {
      name: "Modifier",
      logoName: "pen",
      action: editRequestsCost,
    },
    {
      name: "Traiter",
      logoName: "edit",
      action: processRequests,
    },
    {
      name: "Refuser",
      logoName: "cancel",
      action: refuseRequests,
    },
    {
      name: "Mettre en pause",
      logoName: "pause",
      action: pauseRequests,
    },
    {
      name: "Annuler",
      logoName: "close_custom",
      action: cancelRequests,
    },
    {
      name: "Supprimer",
      logoName: "delete_trash",
      action: removeRequests,
    },
  ];

  const removeFootersActions: IFooterActions[] = [
    {
      name: "Supprimer",
      logoName: "delete_trash",
      action: removeRequests,
    },
  ];

  function editRequestsCost() {
    const requestStore = useRequestsStore();
    requestStore.showEditCostModal = true;
  }

  function refuseRequests() {
    const requestStore = useRequestsStore();
    requestStore.showRefuseModal = true;
  }

  function cancelRequests() {
    const requestStore = useRequestsStore();
    requestStore.showCancelModal = true;
  }

  function removeRequests() {
    const requestStore = useRequestsStore();
    requestStore.showRemoveModal = true;
  }

  function pauseRequests() {
    const requestStore = useRequestsStore();
    requestStore.showPauseModal = true;
  }

  // Checked at the toolbar only: the duplicates modal re-enters showAddProjectFunnel on a selection already checked.
  async function processRequests(selectedCount: number) {
    if (!useCreateProjectStore().canAddToPlan(selectedCount)) return;
    await showAddProjectFunnel();
  }

  async function showAddProjectFunnel() {
    const createProjectCoreStore = useCreateProjectStore();
    const requestStore = useRequestsStore();
    const { project } = storeToRefs(createProjectCoreStore);
    const selectedUsers: IGetSelectedRequests = await requestStore.getSelectedUsers();

    project.value.sourceType = ItemType.REQUEST;

    if (selectedUsers.requestsOfUsersWithMultipleRequests.length) {
      requestStore.duplicatedRequests = selectedUsers.requestsOfUsersWithMultipleRequests;
      requestStore.showDuplicatedModal = true;
    } else {
      const recommendations = await requestStore.fetchBookingRecommendations(
        selectedUsers.selectedRequests.map((request) => request.requestId),
      );
      // Closed only once the next modal is known: closing it before the lookup flashes the page in between.
      requestStore.showDuplicatedModal = false;

      if (recommendations.length) {
        requestStore.bookingRecommendations = recommendations;
        requestStore.requestsToProcess = selectedUsers.selectedRequests;
        requestStore.showBookingRecommendationsModal = true;
      } else {
        openRequestsFunnel(selectedUsers.selectedRequests);
      }
    }
  }

  function openRequestsFunnel(requests: SelectedRequestUser[]) {
    const createProjectCoreStore = useCreateProjectStore();
    const { project } = storeToRefs(createProjectCoreStore);

    createProjectCoreStore.openProjectModal();

    /** we need to set the source_type and source_id after the openProjectModal because the project is reset */
    project.value.sourceType = ItemType.REQUEST;
    project.value.projectType = ProjectTypeEnum.NEW_PROJECT;

    project.value.users.usersId = requests;
  }

  /**
   * The company's request-form template, or null when the feature is off or unset. Returned rather
   * than written into a caller-shaped object: the two flows that use it disagree on casing
   * (`employeeComment` on the request form, `employee_comment` off-catalog), and a shared mutator
   * would have to carry both.
   */
  async function getEmployeeCommentTemplate(): Promise<string | null> {
    const createCompaniesStore = useCreateCompaniesStore();
    const authStore = useAuthStore();
    if (authStore.user?.mainEntityId?.companyId) {
      await createCompaniesStore.fetchRequestFormSettings(authStore.user.mainEntityId.companyId);
    }
    const { requestFormTemplateEnabled, requestFormTemplate } = createCompaniesStore.company;
    return requestFormTemplateEnabled && requestFormTemplate?.trim() ? requestFormTemplate : null;
  }

  /**
   * Whether the viewer can still act on the open request — the gate of « Traiter la demande » and of
   * every alternative to it. A processed request is locked unless it only awaits a priority
   * confirmation, or was put on hold.
   */
  const canProcessRequest = computed(() => {
    const authStore = useAuthStore();
    const { requestDetails } = storeToRefs(useRequestsStore());
    if (!authStore.isCompanyAdmin && !authStore.isGroupAdmin) return false;

    const isLocked =
      !requestDetails.value?.notProcessed &&
      requestDetails.value?.status !== RequestStatus.WAITING_PRIORITY_CONFIRMATION;
    return !isLocked || requestDetails.value?.status === RequestStatus.ON_HOLD;
  });

  return {
    headers,
    removeFootersActions,
    statusMapping,
    getRequestPriority,
    footersActions,
    showAddProjectFunnel,
    openRequestsFunnel,
    getEmployeeCommentTemplate,
    canProcessRequest,
  };
};

export const useRequestsValidation = () => {
  async function validateManagerRequestsCreation() {
    const createRequestStore = useCreateRequestStore();
    const { request, isCheckedForOther } = storeToRefs(createRequestStore);
    const authStore = useAuthStore();

    const mandatoryFieldMessage = "Ce champ est obligatoire";

    const rules = computed(() => {
      return {
        // `isGroupAdmin` belongs here: every other branch of the request flow treats group_admin as a
        // requester (the attendee picker renders for it, `createRequest()` routes it to
        // `createOtherRequest()`, the off-catalog footer sends it to the manager step). This condition
        // was the ONLY place omitting it, so a group_admin got no rules at all — no error was set,
        // `checkCreateOtherRequestError` stayed false, and `createOtherRequest`'s guard then
        // short-circuited on the empty `employee_comment`: no message and no request, just a dead
        // button. Reachable only for a group_admin without manager/company_admin/group_viewer.
        ...((authStore.isManager || authStore.isCompanyAdmin || authStore.isGroupViewer || authStore.isGroupAdmin) && {
          usersToInvite: {
            required: helpers.withMessage("Sélectionner au moins une personne", required),
          },
          employeeComment: {
            required: helpers.withMessage(mandatoryFieldMessage, required),
          },
        }),
        ...(((!request.value.usersToInvite.length && isCheckedForOther.value) ||
          (request.value.usersToInvite.length &&
            request.value.usersToInvite.some((u) => u.userId !== authStore.user?.userId))) && {
          managerPriority: {
            required: helpers.withMessage(mandatoryFieldMessage, required),
          },
        }),
      };
    });

    const v$ = useVuelidate(rules, createRequestStore.request);
    await v$.value.$validate();

    return v$;
  }

  async function validateRequestsCreation() {
    const createRequestStore = useCreateRequestStore();
    const { request } = storeToRefs(createRequestStore);

    const mandatoryFieldMessage = "Ce champ est obligatoire";

    const rules = computed(() => {
      return {
        employeeComment: {
          required: helpers.withMessage(mandatoryFieldMessage, required),
        },
      };
    });

    const v$ = useVuelidate(rules, request);
    await v$.value.$validate();

    return v$;
  }

  return { validateManagerRequestsCreation, validateRequestsCreation };
};
